import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Church, ShieldCheck, Volume2, VolumeX, X, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { resolveMediaUrl } from "@/components/FileUpload";
import { StoryStage } from "@/components/StoryStage";
import { useBackHandler } from "@/lib/backButton";
import { useVolumeKeyUnmute, VOLUME_PRIORITY } from "@/lib/volumeKeys";
import { escapeConsumed } from "@/lib/escapeLayer";
import { storyTimeLabel } from "@/lib/timeAgo";
import { imageVariant } from "@/lib/mediaUrl";
import { schedulePrefetch, upcomingStories } from "@/lib/storyPrefetch";
import { useApp } from "@/context/AppContext";
import { isMusicTimedStory, musicStorySeconds, useMusicStoryProgress } from "@/lib/musicStoryProgress";

const STORY_DURATION_MS = 5000;

// Visionneuse des annonces d'une église (page d'église) : MÊME rendu que le Fil (StoryStage :
// fond/média, texte ajusté, dessin, stickers, carte de lien), segments de progression, ‹ › entre
// les annonces (flèches PC + zones de tap téléphone + clavier), son, suppression par l'auteur.
export const ChurchStoryViewer = ({ stories, index, onIndexChange, onClose, church, user, t, onDeleted }) => {
  const story = stories[index] || null;
  const { config } = useApp();
  const musicTimed = isMusicTimedStory(story); // lot 619 — photo/texte + musique : durée calée sur la musique
  const [muted, setMuted] = useState(true);
  // Lot 638 — touche de volume physique → son de la story d'église activé.
  useVolumeKeyUnmute(true, VOLUME_PRIORITY.STORY, () => setMuted(false));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const musicRef = useRef(null);
  const canDelete = !!(user && story && (story.author_id === user.id || user.role === "super_admin"));
  const hasPrev = index > 0;
  const hasNext = index < stories.length - 1;
  const prev = () => { if (hasPrev) onIndexChange(index - 1); };
  const next = () => { if (hasNext) onIndexChange(index + 1); else onClose(); };

  useBackHandler(true, () => { if (confirmDelete) setConfirmDelete(false); else onClose(); return true; });

  // Enregistre la vue (comme le Fil) + avance automatique (images/texte ; les vidéos avancent à la fin).
  useEffect(() => {
    if (!story) return undefined;
    if (user && story.author_id !== user.id) api.post(`/stories/${story.id}/view`).catch(() => {});
    if (story.media_type === "video" || musicTimed || confirmDelete) return undefined;
    const tm = setTimeout(next, STORY_DURATION_MS);
    return () => clearTimeout(tm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [story?.id, confirmDelete]);

  const musicPct = useMusicStoryProgress({
    active: !!story && musicTimed, audioRef: musicRef, storyKey: story?.id, startAt: story?.music_start || 0,
    limitSec: musicStorySeconds(config), paused: confirmDelete, onDone: next,
  });

  // Lot 593 — précharge l'annonce suivante pendant que celle-ci est à l'écran.
  useEffect(() => schedulePrefetch(upcomingStories([{ stories }], 0, index)), [stories, index]);

  // Musique de la story (version simple : lecture au volume choisi depuis le point de départ).
  useEffect(() => {
    const a = musicRef.current;
    if (!a) return undefined;
    if (story?.music_url) {
      const startAt = story.music_start || 0;
      a.src = resolveMediaUrl(story.music_url);
      a.currentTime = startAt;
      a.loop = true;
      a.volume = typeof story.music_volume === "number" ? story.music_volume : 1;
      a.muted = muted;
      a.play().catch(() => {});
      return () => { try { a.pause(); } catch { /* noop */ } };
    }
    return undefined;
  }, [story?.id, story?.music_url, story?.music_start, story?.music_volume, muted]);

  // Clavier PC : ← → naviguent, Échap ferme.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { if (escapeConsumed(e)) return; e.preventDefault(); if (confirmDelete) setConfirmDelete(false); else onClose(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); next(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, stories.length, confirmDelete]);

  const performDelete = async () => {
    if (!story || deleting) return;
    setDeleting(true);
    try {
      await api.delete(`/stories/${story.id}`);
      toast.success(t("story.deleted", "Story supprimée"));
      setConfirmDelete(false);
      onDeleted?.(story.id);
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setDeleting(false); }
  };

  if (!story) return null;
  const arrowCls = "hidden sm:grid relative z-20 h-11 w-11 shrink-0 place-items-center rounded-full bg-white/15 text-white backdrop-blur-sm transition-colors hover:bg-white/30";
  return (
    <div className="fixed inset-0 z-[70] bg-black grid place-items-center" onClick={onClose} data-testid="church-story-viewer">
      <div className="flex items-center justify-center gap-4" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={prev} aria-label={t("story.prev", "Story précédente")} className={`${arrowCls} ${hasPrev ? "" : "invisible pointer-events-none"}`} data-testid="church-story-prev">
          <ChevronLeft size={24} />
        </button>
        <div className="story-viewer-card overflow-hidden bg-black relative sm:rounded-2xl sm:shadow-2xl" data-testid="church-story-media">
          <StoryStage story={story} muted={muted} onVideoEnded={next} safeTop="4.5rem" safeBottom="4rem" testIdPrefix="church-story" />

          {/* Zones de tap (téléphone) : tiers gauche = précédente, reste = suivante — sous l'en-tête et au-dessus du pied. */}
          <div className="absolute left-0 right-0 z-10 flex sm:hidden" style={{ top: "calc(var(--sa-top, 0px) + 4rem)", bottom: "calc(var(--sa-bottom, 0px) + 4rem)" }} data-testid="church-story-tap-zones">
            <button type="button" aria-label={t("story.prev", "Story précédente")} className="w-1/3 h-full" onClick={prev} />
            <button type="button" aria-label={t("story.next", "Story suivante")} className="flex-1 h-full" onClick={next} />
          </div>

          <div className="absolute left-2 right-2 flex gap-1 z-10" style={{ top: "calc(var(--sa-top, 0px) + 8px)" }} data-testid="church-story-progress">
            {stories.map((s, i) => (
              <div key={s.id} className="h-1 flex-1 rounded-full bg-white/30 overflow-hidden">
                {i < index ? <div className="h-full bg-white" style={{ width: "100%" }} />
                  : i > index ? null
                  : story.media_type === "video" ? <div className="h-full bg-white/60" style={{ width: "100%" }} />
                  : musicTimed ? <div className="h-full bg-white" style={{ width: `${musicPct}%` }} data-testid="church-story-progress-music" />
                  : <div key={`seg-${story.id}`} className="h-full bg-white" style={{ animation: `storyProgress ${STORY_DURATION_MS}ms linear forwards`, animationPlayState: confirmDelete ? "paused" : "running" }} />}
              </div>
            ))}
          </div>

          <div className="absolute left-3 right-3 flex items-center gap-2 text-white z-20" style={{ top: "calc(var(--sa-top, 0px) + 20px)" }}>
            {church?.logo_url ? (
              <img src={imageVariant(church.logo_url, 48)} alt="" className="h-9 w-9 rounded-full object-cover border-2 border-white shrink-0" />
            ) : (
              <div className="h-9 w-9 rounded-full grid place-items-center border-2 border-white shrink-0" style={{ background: "var(--yalola-primary)" }}><Church size={17} className="text-white" /></div>
            )}
            <div className="min-w-0">
              <div className="text-sm font-semibold flex items-center gap-1 truncate" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}>
                <span className="truncate">{church?.name}</span><ShieldCheck size={14} className="shrink-0" />
              </div>
              <div className="text-[10px] opacity-90 leading-none" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}>{t("story.official_church", "Église officielle")} · {storyTimeLabel(story.created_at, localStorage.getItem("yalola.lang") || "fr", t)}</div>
            </div>
            <div className="ml-auto flex items-center gap-2 shrink-0">
              {(story.media_type === "video" || story.music_url) && (
                <button type="button" onClick={() => setMuted((m) => !m)} className="text-white bg-black/40 rounded-full h-9 w-9 grid place-items-center" data-testid="church-story-sound">
                  {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                </button>
              )}
              <button type="button" onClick={onClose} className="text-white bg-black/40 hover:bg-black/60 rounded-full h-9 w-9 grid place-items-center transition-colors" data-testid="church-story-close" aria-label={t("common.close", "Fermer")}>
                <X size={18} />
              </button>
            </div>
          </div>

          <div className="absolute left-3 right-3 z-20 flex items-center justify-between gap-2" style={{ bottom: "calc(var(--sa-bottom, 0px) + 12px)" }}>
            {canDelete && typeof story.views_count === "number" ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-black/45 text-white px-3 py-1.5 text-xs font-semibold" data-testid="church-story-views"><Eye size={14} /> {t("story.viewed_by", "Vu par {count}").replace("{count}", story.views_count)}</span>
            ) : <span />}
            {canDelete && (
              <button type="button" onClick={() => setConfirmDelete(true)} className="text-white bg-black/45 hover:bg-red-600 rounded-full h-9 w-9 grid place-items-center transition-colors" data-testid="church-story-delete" aria-label={t("story.delete", "Supprimer")}>
                <Trash2 size={16} />
              </button>
            )}
          </div>

          {confirmDelete && (
            <div className="absolute inset-0 z-[80] flex items-center justify-center p-6" data-testid="church-story-delete-overlay">
              <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setConfirmDelete(false)} />
              <div className="relative w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl">
                <h3 className="text-lg font-bold" style={{ color: "var(--yalola-text)" }}>{t("story.delete_title", "Supprimer cette story ?")}</h3>
                <p className="text-sm mt-1.5" style={{ color: "var(--yalola-text-muted)" }}>{t("story.delete_desc", "Cette action est définitive. La story et ses statistiques de vues seront supprimées.")}</p>
                <div className="flex justify-end gap-2 mt-5">
                  <button type="button" onClick={() => setConfirmDelete(false)} className="rounded-full px-4 py-2 text-sm font-semibold" style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text)" }} data-testid="church-story-delete-cancel">{t("common.cancel", "Annuler")}</button>
                  <button type="button" onClick={performDelete} disabled={deleting} className="rounded-full px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-60" data-testid="church-story-delete-confirm">{t("story.delete", "Supprimer")}</button>
                </div>
              </div>
            </div>
          )}

          <audio ref={musicRef} className="hidden" />
        </div>
        <button type="button" onClick={next} aria-label={t("story.next", "Story suivante")} className={`${arrowCls} ${hasNext ? "" : "invisible pointer-events-none"}`} data-testid="church-story-next">
          <ChevronRight size={24} />
        </button>
      </div>
    </div>
  );
};
